import React from 'react';
import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';
import { Flex, Switch, Typography, theme } from 'antd';
import dayjs from 'dayjs';

import { Colors } from '~library/prerenderTheme';
import { numberFormatter } from '~library/numbers';
import { MainChartLegend, ChartCard } from '~components/Charts';
import PagesRenderedMainChart, { MAIN_CHART_ID } from './PagesRenderedMainChart';
import { describeDateRange } from '~components/Filters';
import { useChartItemsVisibility, type ChartConfig } from '~library/chartjs';
import useTrackDashboardV3Event from '../useTrackDashboardV3Event';
import { type WidgetLoadError } from '~features/empty-states';
import ChartLoadError from '../components/ChartLoadError';

export const PAGES_RENDERED_VISIBILITY_CHARTS: ChartConfig[] = [{ id: MAIN_CHART_ID, type: 'dataset' }];

export const TOOLTIP_MESSAGE =
  'The number of pages rendered over the selected period. Higher numbers mean more optimised pages and improved crawl efficiency.<br />Note: Includes renders triggered by crawler requests for uncached pages, as well as scheduled renders.';

export const HIDE_SCHEDULED_RENDERS_TOOLTIP_MESSAGE =
  'The number of pages rendered over the selected period. Higher numbers mean more optimised pages and improved crawl efficiency.<br />Note: Scheduled renders are hidden, so this chart only includes renders triggered by crawler requests for uncached pages.';

export function getPagesRenderedTooltipMessage(includeScheduledRenders = true) {
  return includeScheduledRenders ? TOOLTIP_MESSAGE : HIDE_SCHEDULED_RENDERS_TOOLTIP_MESSAGE;
}

export type ChartPagesRenderedProps = {
  rawData?: AggregatedMetrics.PagesRenderedResult;
  isLoading: boolean;
  error?: WidgetLoadError;
  dateRange: [Date, Date];
  descriptionVisible?: boolean;
  includeScheduledRenders?: boolean;
  onIncludeScheduledRendersChange: (value: boolean) => void;
};

function ScheduledRendersSwitch({
  includeScheduledRenders,
  onIncludeScheduledRendersChange,
}: {
  includeScheduledRenders: boolean;
  onIncludeScheduledRendersChange: (value: boolean) => void;
}) {
  const track = useTrackDashboardV3Event();
  const { token } = theme.useToken();

  return (
    <Flex align="center" gap={token.marginXS} style={{ marginTop: token.marginSM }}>
      <Switch
        aria-label="Hide scheduled renders"
        checked={!includeScheduledRenders}
        onChange={(checked) => {
          track('ScheduledRendersToggled', {
            action: checked ? 'hidden' : 'shown',
          });
          onIncludeScheduledRendersChange(!checked);
        }}
      />
      <Typography.Text strong>Hide scheduled renders</Typography.Text>
    </Flex>
  );
}

function ChartPagesRenderedCard({
  rawData,
  isLoading,
  error,
  dateRange,
  descriptionVisible = false,
  includeScheduledRenders = true,
  onIncludeScheduledRendersChange,
}: ChartPagesRenderedProps) {
  const activeFilterLabel = React.useMemo(() => describeDateRange(dayjs(dateRange[0]), dayjs(dateRange[1])), [dateRange]);

  return (
    <ChartCard
      isLoading={isLoading}
      errorFallback={error && <ChartLoadError onRetry={error.onRetry} />}
      title={`Pages Rendered (${activeFilterLabel})`}
      description={getPagesRenderedTooltipMessage(includeScheduledRenders)}
      descriptionType={descriptionVisible ? 'paragraph' : 'tooltip'}
    >
      {rawData && (
        <ChartPagesRendered
          rawData={rawData}
          includeScheduledRenders={includeScheduledRenders}
          onIncludeScheduledRendersChange={onIncludeScheduledRendersChange}
        />
      )}
    </ChartCard>
  );
}

function ChartPagesRendered({
  rawData,
  includeScheduledRenders = true,
  onIncludeScheduledRendersChange,
}: {
  rawData: AggregatedMetrics.PagesRenderedResult;
  includeScheduledRenders?: boolean;
  onIncludeScheduledRendersChange: (value: boolean) => void;
}) {
  const track = useTrackDashboardV3Event();
  const { itemsVisibility, toggleVisibility, restoreVisibility } = useChartItemsVisibility(
    PAGES_RENDERED_VISIBILITY_CHARTS
  );
  const { token } = theme.useToken();

  const summary = React.useMemo(() => {
    const data = rawData.data.reduce<number[]>((acc, datapoint, index) => {
      acc[index] = datapoint.count;
      return acc;
    }, Array(rawData.intervals.length).fill(0));

    return {
      total: rawData.data.reduce((acc, datapoint) => (acc += datapoint.count), 0),
      dataset: {
        label: 'Pages rendered',
        data,
        backgroundColor: Colors.Purple5,
      },
    };
  }, [rawData.data, rawData.intervals.length]);

  const onLegendItemClicked = (datasetIndex: number) => {
    track('ChartDatasetToggled', {
      chart: 'pages rendered',
      dataset: 'pages rendered',
      action: itemsVisibility[datasetIndex] ? 'hide' : 'show',
    });
    toggleVisibility(datasetIndex);
  };

  React.useEffect(() => {
    restoreVisibility(1);
  }, [restoreVisibility, summary.dataset]);

  return (
    <ChartCard.Content>
      <Flex justify="flex-start" align="flex-start" gap={token.marginMD} wrap="wrap">
        <MainChartLegend>
          <MainChartLegend.LabelBig
            label="Pages rendered"
            labelColor={Colors.Purple5}
            value={`${numberFormatter.format(summary.total)}`}
            crossedOut={!itemsVisibility[0]}
            onClick={() => onLegendItemClicked(0)}
          />
        </MainChartLegend>
        <ScheduledRendersSwitch
          includeScheduledRenders={includeScheduledRenders}
          onIncludeScheduledRendersChange={onIncludeScheduledRendersChange}
        />
      </Flex>
      <ChartCard.ChartLayout>
        <PagesRenderedMainChart
          granularity={rawData.granularity}
          labels={rawData.intervals}
          datasets={[summary.dataset]}
        />
      </ChartCard.ChartLayout>
    </ChartCard.Content>
  );
}
export default ChartPagesRenderedCard;
